import { ArrowType } from '@ardoq/api-types';
import { Bounds, Vector } from '@ardoq/graph';
import { BlocksReference } from '../types';
import { rectHeight, rectWidth } from 'tabview/visualizations/misc/geometry';
import { RenderContext } from './types';
import { AQBLOCKS_REFERENCE_CORNERCLAMP } from '../consts';
import type { Point } from '@ardoq/math';
import {
  markerShapes,
  traceMarkerShapes,
  type MarkerPlacement,
} from 'tabview/canvasRendering/markerShapes';

/**
 * Where a marker's shapes are drawn: the projected, pixel-snapped anchor, and the
 * direction the reference arrives from. `null` when the marker would not be visible —
 * `ArrowType.NONE`, or a `referenceMarkerSize` below the current `lineWidth`, where it
 * would not be distinguishable from the line itself.
 *
 * @param prev  the previous route waypoint, in world coordinates, used only
 *   to determine the marker's orientation.
 * @param end  the route endpoint where the marker is anchored, in world
 *   coordinates.
 */
const placeMarker = (
  prev: Vector,
  end: Vector,
  arrowType: ArrowType,
  params: RenderContext
): MarkerPlacement | null => {
  const size = params.referenceMarkerSize;

  if (arrowType === ArrowType.NONE) {
    return null;
  }

  const anchor = params.projection.projectPointSnapped(end);
  const from = params.projection.projectPointSnapped(prev);

  /* Exactly zero rather than whatever `atan2` returns for a route that is horizontal to
     the eye. The anchor is pixel-snapped, and turning it by a few float-epsilons of angle
     would put it back off the pixel grid. */
  return {
    anchor,
    angle: pointsRight(anchor, from)
      ? 0
      : Math.atan2(anchor[1] - from[1], anchor[0] - from[0]),
    size,
  };
};

/** Half a pixel of slop, so a route that is horizontal to the eye counts as horizontal. */
const AXIS_EPSILON = 0.5;

const pointsRight = ([x, y]: Point, [fromX, fromY]: Point): boolean =>
  Math.abs(y - fromY) <= AXIS_EPSILON && x > fromX + AXIS_EPSILON;

export const beginMarkerPlacement = (
  reference: BlocksReference,
  route: Vector[],
  params: RenderContext
): MarkerPlacement | null =>
  placeMarker(route[1], route[0], reference.lineBeginning, params);

export const endMarkerPlacement = (
  reference: BlocksReference,
  route: Vector[],
  params: RenderContext
): MarkerPlacement | null =>
  placeMarker(
    route[route.length - 2],
    route[route.length - 1],
    reference.lineEnding,
    params
  );

const trace = (
  arrowType: ArrowType,
  placement: MarkerPlacement | null,
  params: RenderContext
): void => {
  if (placement) {
    traceMarkerShapes(params.ctx, markerShapes(arrowType), placement);
  }
};

/**
 * Adds the begin-marker (start of the route) to the current canvas path, pinned at
 * `route[0]` with orientation derived from `route[1] → route[0]`. The path is *added*
 * to the current path; the caller owns `beginPath()` / `stroke()` around the call.
 */
export const createBeginMarkerPath = (
  reference: BlocksReference,
  route: Vector[],
  params: RenderContext
): void =>
  trace(
    reference.lineBeginning,
    beginMarkerPlacement(reference, route, params),
    params
  );

/**
 * Adds the end-marker to the current canvas path, pinned at `route[length-1]` with
 * orientation derived from `route[length-2] → route[length-1]`. See
 * `createBeginMarkerPath` for the path-state contract.
 */
export const createEndMarkerPath = (
  reference: BlocksReference,
  route: Vector[],
  params: RenderContext
): void =>
  trace(
    reference.lineEnding,
    endMarkerPlacement(reference, route, params),
    params
  );

/**
 * Adds the polyline that traces the route to the current canvas path. Caller
 * is responsible for `beginPath()` / `stroke()` around the call.
 *
 * Three rendering modes, selected by context:
 *   1. **Detail-cull fallback** — when either dimension of `bounds` is below
 *      ~2 device pixels, the entire route is drawn as a single straight
 *      segment from the first to the last waypoint. Cheaper, and the visual
 *      result is indistinguishable at this scale.
 *   2. **Rounded corners** — when `params.referenceCornerRadius > 2`, each
 *      waypoint becomes a small `arcTo` corner, with the radius clamped per
 *      segment to one third of the segment length so adjacent corners can't
 *      overlap.
 *   3. **Square corners** — straight `lineTo`s between waypoints. Used when
 *      the corner radius is below the threshold (zoomed-out or tiny lines).
 *
 * All canvas coordinates are produced via `projectPointSnapped` so segment
 * ends land on pixel centres (avoids the 1px-wide-line-rendered-as-2px-fuzz
 * issue on non-Retina screens).
 */
export const createRoutePath = (
  _reference: BlocksReference,
  bounds: Bounds,
  route: Vector[],
  params: RenderContext
) => {
  const detailCull = 2 * params.pixel();
  const { ctx, projection } = params;

  if (rectWidth(bounds) < detailCull || rectHeight(bounds) < detailCull) {
    const pt0 = projection.projectPointSnapped(route[0]);
    const pt1 = projection.projectPointSnapped(route[route.length - 1]);

    ctx.moveTo(...pt0);
    ctx.lineTo(...pt1);

    return;
  }

  const routeCornerRadius = params.referenceCornerRadius;

  if (routeCornerRadius > AQBLOCKS_REFERENCE_CORNERCLAMP[0]) {
    let i0 = 0;
    let pi0: Vector = projection.projectPointSnapped(route[i0]);
    let r0 = 0;
    const limit = 0.25;

    ctx.moveTo(...pi0);

    for (let i = 1; i < route.length; ++i) {
      const pi = projection.projectPointSnapped(route[i]);
      const v = [pi[0] - pi0[0], pi[1] - pi0[1]];

      if (v[0] === 0 && v[1] === 0) {
        continue;
      }

      const mag = Math.hypot(v[0], v[1]);

      if (mag < 1) {
        continue;
      }

      const r = Math.min(mag / 3, routeCornerRadius);

      v[0] = (r * v[0]) / mag;
      v[1] = (r * v[1]) / mag;

      if (r < limit) {
        ctx.lineTo(pi0[0] + v[0], pi0[1] + v[1]);
      } else {
        ctx.arcTo(
          pi0[0],
          pi0[1],
          pi0[0] + v[0],
          pi0[1] + v[1],
          Math.min(r, r0)
        );
      }

      ctx.lineTo(pi[0] - v[0], pi[1] - v[1]);
      i0 = i;
      pi0 = pi;
      r0 = r;
    }

    ctx.lineTo(...projection.projectPointSnapped(route[route.length - 1]));
  } else {
    /* square corners */
    ctx.moveTo(...projection.projectPointSnapped(route[0]));
    for (let i = 1; i < route.length; ++i) {
      ctx.lineTo(...projection.projectPointSnapped(route[i]));
    }
  }
};
